import type { JSX, ReactNode } from "react";
import { createContext, createRef, useContext } from "react";
import type { PropsWithChildren } from "react";
import type { PopperProps } from "react-popper";

import type { CustomEmoji, EmojiData } from ".";
import { TAB_ITEMS } from "./constants";
import { useEmojiPicker } from "./hooks/useEmojiPicker";
import type { EmojiPickerState } from "./hooks/useEmojiPicker";

export interface EmojiPickerProviderValues {
  as?: keyof JSX.IntrinsicElements;
  buttonAriaLabel?: string;
  customIcons?: CustomEmoji[] | ReadonlyArray<CustomEmoji>;
  customShapeIcons?: CustomEmoji[] | ReadonlyArray<CustomEmoji>;
  handleCustomIconUpload?: ({
    status,
    progress,
    response,
    isError,
  }: any) => void;
  name?: string;
  onEmojiSelect?: (emojiData: EmojiData) => void;
  onRemoveEmoji?: () => void;
  popoverPanelClassName?: string;
  popoverRootClassName?: string;
  portalTo?: HTMLElement | null;
  shouldCloseOnSelect?: boolean;
  shouldEmojiPickerRenderAsPortal?: boolean;
  shouldShowEmojiPreview?: boolean;
  shouldShowSkinTonePicker?: boolean;
  target?: ReactNode;
  targetWrapperClassName?: string;
  value?: any;
}

export interface EmojiPickerProviderProps {
  pickerAddOns: string[];
  placement?: PopperProps<any>["placement"];
  popperOptions?: PopperProps<any>["modifiers"];
  value: EmojiPickerProviderValues;
}

interface EmojiPickerContextValue
  extends
    EmojiPickerProviderValues,
    Omit<
      EmojiPickerState,
      | "setHasElementMutated"
      | "hasElementMutated"
      | "targetElement"
      | "popperElement"
    > {
  popoverPanelClassName?: string;
  popoverRootClassName?: string;
  shouldEmojiPickerRenderAsPortal?: boolean;
  shouldShowEmojiPreview?: boolean;
  target?: ReactNode;
  targetWrapperClassName?: string;
}

const EmojiPickerContext = createContext<EmojiPickerContextValue>({
  activeTab: TAB_ITEMS[0],
  setActiveTab: () => {},
  setPopperElement: () => {},
  setTargetElement: () => {},
  emojiPickerPopoverRef: createRef(),
  isEmojiPickerTabActive: false,
  isIconPickerTabActive: false,
  isShapePickerTabActive: false,
  isCustomPickerTabActive: false,
  shouldRenderEmojiMartPicker: false,
  styles: {},
  attributes: {},
  EMOJI_PICKER_ENABLED_TABS_MAPPER: {},
  isCustomFileUploadProcessing: false,
  setIsCustomFileUploadProcessing: () => {},
});
EmojiPickerContext.displayName = "EmojiPickerContext";

export const EmojiPickerProvider = ({
  pickerAddOns,
  placement,
  popperOptions,
  children,
  value,
}: PropsWithChildren<EmojiPickerProviderProps>) => {
  const {
    activeTab,
    setActiveTab,
    setPopperElement,
    setTargetElement,
    emojiPickerPopoverRef,
    isEmojiPickerTabActive,
    isIconPickerTabActive,
    isShapePickerTabActive,
    isCustomPickerTabActive,
    shouldRenderEmojiMartPicker,
    styles,
    attributes,
    EMOJI_PICKER_ENABLED_TABS_MAPPER,
    isCustomFileUploadProcessing,
    setIsCustomFileUploadProcessing,
  } = useEmojiPicker({ pickerAddOns, placement, popperOptions });

  return (
    <EmojiPickerContext.Provider
      value={{
        activeTab,
        setActiveTab,
        setPopperElement,
        setTargetElement,
        emojiPickerPopoverRef,
        isEmojiPickerTabActive,
        isIconPickerTabActive,
        isShapePickerTabActive,
        isCustomPickerTabActive,
        shouldRenderEmojiMartPicker,
        styles,
        attributes,
        EMOJI_PICKER_ENABLED_TABS_MAPPER,
        isCustomFileUploadProcessing,
        setIsCustomFileUploadProcessing,
        ...value,
      }}
    >
      {children}
    </EmojiPickerContext.Provider>
  );
};

export const useEmojiPickerContext = () => useContext(EmojiPickerContext);
